import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppDispatch, useAppSelector } from '../../store';
import UserSlice from '../../store/slices/User';

// PACKAGES
import { Link, useLocation } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import ScrollToTopButton from '../../components/ScrollToTopButton';
import StatusIcon from '../../components/StatusIcon';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import DisplayDateTime from '../../components/DisplayDateTime';
import DisplayDateTimeNoParse from '../../components/DisplayDateTimeNoParse';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';
import { ReactComponent as SwitchUserIcon } from '../../assets/svg/switch-user.svg';
import { ReactComponent as SwitchIcon } from '../../assets/svg/switch.svg';

// TYPES
import {
	SafetyChecklistAdminDashboardDashboard,
	SafetyChecklistUserDashboardDashboard,
	DomainChecklistStatusType
} from '../../api/models';

const SafetyChecklists: React.FC = () => {
	const dispatch = useAppDispatch();
	const location = useLocation();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Page size for pagination
	const defaultMaxItems: number = 10;

	// Check if User is viewing Checklist as an Admin or not
	const viewChecklistAsAdmin = useAppSelector((state) => state.user.viewChecklistAsAdmin);

	// SET UP ADMIN DATA STATE AND INITIAL ADMIN DATA
	const initialAdminData: SafetyChecklistAdminDashboardDashboard = {
		toDoList: [],
		overdueList: [],
		manageList: [],
		completedList: []
	};

	const [adminSafetyChecklistsData, setAdminSafetyChecklistsData] =
		React.useState<SafetyChecklistAdminDashboardDashboard>(initialAdminData);

	// Admin: total counts and current page per section (for load more)
	const [adminToDoTotalCount, setAdminToDoTotalCount] = React.useState<number>(0);
	const [adminOverdueTotalCount, setAdminOverdueTotalCount] = React.useState<number>(0);
	const [adminManageTotalCount, setAdminManageTotalCount] = React.useState<number>(0);
	const [adminCompletedTotalCount, setAdminCompletedTotalCount] = React.useState<number>(0);

	const [adminPageToDo, setAdminPageToDo] = React.useState<number>(1);
	const [adminPageOverdue, setAdminPageOverdue] = React.useState<number>(1);
	const [adminPageManage, setAdminPageManage] = React.useState<number>(1);
	const [adminPageCompleted, setAdminPageCompleted] = React.useState<number>(1);

	const [loadingMoreToDoAdmin, setLoadingMoreToDoAdmin] = React.useState<boolean>(false);
	const [loadingMoreOverdueAdmin, setLoadingMoreOverdueAdmin] = React.useState<boolean>(false);
	const [loadingMoreManageAdmin, setLoadingMoreManageAdmin] = React.useState<boolean>(false);
	const [loadingMoreCompletedAdmin, setLoadingMoreCompletedAdmin] = React.useState<boolean>(false);

	const getAdminSafetyChecklistsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getAdminSafetyChecklistDashboard(1, 1, 1, 1);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialAdminData } = response;
			setIsLoading(false);

			setAdminSafetyChecklistsData(result);

			setAdminToDoTotalCount(result.toDoTotalCount ?? 0);
			setAdminOverdueTotalCount(result.overdueTotalCount ?? 0);
			setAdminManageTotalCount(result.manageTotalCount ?? 0);
			setAdminCompletedTotalCount(result.completedTotalCount ?? 0);

			setAdminPageToDo(1);
			setAdminPageOverdue(1);
			setAdminPageManage(1);
			setAdminPageCompleted(1);
		}
	};

	// SET UP USER DATA STATE AND INITIAL USER DATA
	const initialUserData: SafetyChecklistUserDashboardDashboard = {
		toDoList: [],
		overdueList: [],
		completedList: [],
		manageList: []
	};

	const [userSafetyChecklistsData, setUserSafetyChecklistsData] =
		React.useState<SafetyChecklistUserDashboardDashboard>(initialUserData);

	// User: total counts and current page per section (for load more)
	const [userToDoTotalCount, setUserToDoTotalCount] = React.useState<number>(0);
	const [userOverdueTotalCount, setUserOverdueTotalCount] = React.useState<number>(0);
	const [userManageTotalCount, setUserManageTotalCount] = React.useState<number>(0);
	const [userCompletedTotalCount, setUserCompletedTotalCount] = React.useState<number>(0);

	const [userPageToDo, setUserPageToDo] = React.useState<number>(1);
	const [userPageOverdue, setUserPageOverdue] = React.useState<number>(1);
	const [userPageManage, setUserPageManage] = React.useState<number>(1);
	const [userPageCompleted, setUserPageCompleted] = React.useState<number>(1);

	const [loadingMoreToDoUser, setLoadingMoreToDoUser] = React.useState<boolean>(false);
	const [loadingMoreOverdueUser, setLoadingMoreOverdueUser] = React.useState<boolean>(false);
	const [loadingMoreManageUser, setLoadingMoreManageUser] = React.useState<boolean>(false);
	const [loadingMoreCompletedUser, setLoadingMoreCompletedUser] = React.useState<boolean>(false);

	const getUserSafetyChecklistsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getUserSafetyChecklistDashboard(1, 1, 1, 1);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialUserData } = response;

			setIsLoading(false);

			setUserSafetyChecklistsData(result);

			setUserToDoTotalCount(result.toDoTotalCount ?? 0);
			setUserOverdueTotalCount(result.overdueTotalCount ?? 0);
			setUserManageTotalCount(result.manageTotalCount ?? 0);
			setUserCompletedTotalCount(result.completedTotalCount ?? 0);

			setUserPageToDo(1);
			setUserPageOverdue(1);
			setUserPageManage(1);
			setUserPageCompleted(1);
		}
	};

	// Initial load: align Redux with dashboard deep links, fetch once. Do not set isLoading false here —
	// the callers set it when the API finishes; otherwise empty lists flash before data arrives.
	React.useEffect(() => {
		if (location.pathname === '/safety-checklists/user-view') {
			dispatch(UserSlice.actions.update({ viewChecklistAsAdmin: false }));
			getUserSafetyChecklistsData();
			return;
		}

		if (isOrganisationAdmin && location.pathname === '/safety-checklists/admin-view') {
			dispatch(UserSlice.actions.update({ viewChecklistAsAdmin: true }));
			getAdminSafetyChecklistsData();
			return;
		}

		if (isOrganisationAdmin && viewChecklistAsAdmin) {
			getAdminSafetyChecklistsData();
		} else {
			getUserSafetyChecklistsData();
		}
	}, []);

	// Admin: 4 sections - To-Do, Overdue, Manage, Completed (lists are accumulated when loading more)
	const toDoAdminList = adminSafetyChecklistsData?.toDoList || [];
	const overdueAdminList = adminSafetyChecklistsData?.overdueList || [];
	const manageAdminList = adminSafetyChecklistsData?.manageList || [];
	const completedAdminList = adminSafetyChecklistsData?.completedList || [];

	// User: 4 sections - To-Do, Overdue, Manage, Completed
	const toDoUserList = userSafetyChecklistsData?.toDoList || [];
	const overdueUserList = userSafetyChecklistsData?.overdueList || [];
	const manageUserList = userSafetyChecklistsData?.manageList || [];
	const completedUserList = userSafetyChecklistsData?.completedList || [];

	// Load more: fetch next page for one section and append (no full-page spinner)
	const loadMoreAdminToDo = async () => {
		setLoadingMoreToDoAdmin(true);

		const response = await apibridge.getAdminSafetyChecklistDashboard(
			adminPageToDo + 1,
			adminPageOverdue,
			adminPageManage,
			adminPageCompleted
		);

		if (!response.isError && response.result) {
			setAdminSafetyChecklistsData((prev) => ({
				...prev,
				toDoList: [...(prev.toDoList || []), ...(response.result?.toDoList || [])]
			}));

			setAdminPageToDo((page) => page + 1);
		}

		setLoadingMoreToDoAdmin(false);
	};

	const loadMoreAdminOverdue = async () => {
		setLoadingMoreOverdueAdmin(true);

		const response = await apibridge.getAdminSafetyChecklistDashboard(
			adminPageToDo,
			adminPageOverdue + 1,
			adminPageManage,
			adminPageCompleted
		);

		if (!response.isError && response.result) {
			setAdminSafetyChecklistsData((prev) => ({
				...prev,
				overdueList: [...(prev.overdueList || []), ...(response.result?.overdueList || [])]
			}));
			setAdminPageOverdue((page) => page + 1);
		}

		setLoadingMoreOverdueAdmin(false);
	};

	const loadMoreAdminManage = async () => {
		setLoadingMoreManageAdmin(true);

		const response = await apibridge.getAdminSafetyChecklistDashboard(
			adminPageToDo,
			adminPageOverdue,
			adminPageManage + 1,
			adminPageCompleted
		);

		if (!response.isError && response.result) {
			setAdminSafetyChecklistsData((prev) => ({
				...prev,
				manageList: [...(prev.manageList || []), ...(response.result?.manageList || [])]
			}));

			setAdminPageManage((page) => page + 1);
		}

		setLoadingMoreManageAdmin(false);
	};

	const loadMoreAdminCompleted = async () => {
		setLoadingMoreCompletedAdmin(true);

		const response = await apibridge.getAdminSafetyChecklistDashboard(
			adminPageToDo,
			adminPageOverdue,
			adminPageManage,
			adminPageCompleted + 1
		);

		if (!response.isError && response.result) {
			setAdminSafetyChecklistsData((prev) => ({
				...prev,
				completedList: [...(prev.completedList || []), ...(response.result?.completedList || [])]
			}));

			setAdminPageCompleted((page) => page + 1);
		}

		setLoadingMoreCompletedAdmin(false);
	};

	const loadMoreUserToDo = async () => {
		setLoadingMoreToDoUser(true);

		const response = await apibridge.getUserSafetyChecklistDashboard(
			userPageToDo + 1,
			userPageOverdue,
			userPageCompleted,
			userPageManage
		);

		if (!response.isError && response.result) {
			setUserSafetyChecklistsData((prev) => ({
				...prev,
				toDoList: [...(prev.toDoList || []), ...(response.result?.toDoList || [])]
			}));

			setUserPageToDo((page) => page + 1);
		}

		setLoadingMoreToDoUser(false);
	};

	const loadMoreUserOverdue = async () => {
		setLoadingMoreOverdueUser(true);

		const response = await apibridge.getUserSafetyChecklistDashboard(
			userPageToDo,
			userPageOverdue + 1,
			userPageCompleted,
			userPageManage
		);

		if (!response.isError && response.result) {
			setUserSafetyChecklistsData((prev) => ({
				...prev,
				overdueList: [...(prev.overdueList || []), ...(response.result?.overdueList || [])]
			}));

			setUserPageOverdue((page) => page + 1);
		}

		setLoadingMoreOverdueUser(false);
	};

	const loadMoreUserManage = async () => {
		setLoadingMoreManageUser(true);
		const response = await apibridge.getUserSafetyChecklistDashboard(
			userPageToDo,
			userPageOverdue,
			userPageCompleted,
			userPageManage + 1
		);

		if (!response.isError && response.result) {
			setUserSafetyChecklistsData((prev) => ({
				...prev,
				manageList: [...(prev.manageList || []), ...(response.result?.manageList || [])]
			}));

			setUserPageManage((page) => page + 1);
		}

		setLoadingMoreManageUser(false);
	};

	const loadMoreUserCompleted = async () => {
		setLoadingMoreCompletedUser(true);

		const response = await apibridge.getUserSafetyChecklistDashboard(
			userPageToDo,
			userPageOverdue,
			userPageCompleted + 1,
			userPageManage
		);

		if (!response.isError && response.result) {
			setUserSafetyChecklistsData((prev) => ({
				...prev,
				completedList: [...(prev.completedList || []), ...(response.result?.completedList || [])]
			}));

			setUserPageCompleted((page) => page + 1);
		}

		setLoadingMoreCompletedUser(false);
	};

	// SWITCH ROLE FUNCTION
	const switchRole = () => {
		if (isOrganisationAdmin) {
			if (viewChecklistAsAdmin) {
				dispatch(UserSlice.actions.update({ viewChecklistAsAdmin: false }));

				getUserSafetyChecklistsData();
			} else {
				dispatch(UserSlice.actions.update({ viewChecklistAsAdmin: true }));

				getAdminSafetyChecklistsData();
			}
		}
	};

	return (
		<div className="page-general-layout safety-checklist">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{isOrganisationAdmin && viewChecklistAsAdmin ? (
									<>
										<p className="sub-grey-text mbtom-30">
											Safety checklists are a critical risk management tool to manage safety and minimise the risk of
											harm to children in your organisation. Below, you can view all safety checklists created
											throughout your organisation and create a checklist.
										</p>

										<Alert color="info" className="mb-5">
											<div className="alert-icon">
												<SwitchUserIcon />
											</div>

											<div className="alert-content">
												<p>
													<strong>As a Leader</strong> you can view all safety checklists{' '}
													<strong>created throughout your organisation</strong>.
												</p>

												<p>
													You can also view safety checklists <strong>created by yourself</strong>. Click on{' '}
													<strong>'View my checklists'</strong> to switch roles.
												</p>
											</div>

											<Button className="btn-primary small ml-auto" onClick={() => switchRole()}>
												<SwitchIcon className="link-icon" />
												View my checklists
											</Button>
										</Alert>

										{isOrganisationActive && (
											<div
												className={
													toDoAdminList.length ||
													overdueAdminList.length ||
													manageAdminList.length ||
													completedAdminList.length
														? 'submit-btn-container'
														: 'submit-btn-container empty'
												}
											>
												<Link to="/new-checklist" className="btn-primary submit-btn">
													Create a checklist
												</Link>
											</div>
										)}

										<section className="info-section complaints-page">
											<div className="section-header bold">To-Do</div>
											{toDoAdminList.length > 0 ? (
												<>
													<div className="grid-list">
														{toDoAdminList.map((item) => (
															<div key={item.id} className="grid-list-row">
																<div className="grid-list-row__inner">
																	<div className="grid-cell-icon">
																		<StatusIcon
																			statusType={
																				item.status === DomainChecklistStatusType.Completed ? 'success' : 'warning'
																			}
																		/>
																	</div>

																	<div className="grid-cell-name">{item.purpose}</div>

																	<div className="grid-cell-created">
																		<span className="title body-medium">Created by</span>

																		<span className="name body-medium text-grey-4">{item.creator}</span>
																	</div>

																	<div className="grid-cell-date">
																		<span className="title body-medium">Created</span>

																		<span className="status body-medium text-grey-4">
																			<DisplayDateTime format="D/M/YYYY, h:mm a">{item.createdDate}</DisplayDateTime>
																		</span>
																	</div>

																	<div className="grid-cell-action">
																		<Link className="btn-text pdl-0-mobile" to={`/safety-checklists/${item.id}`}>
																			View
																		</Link>
																	</div>
																</div>
															</div>
														))}
													</div>

													{toDoAdminList.length < adminToDoTotalCount && (
														<Button
															className="btn-secondary btn-show-more"
															onClick={loadMoreAdminToDo}
															disabled={loadingMoreToDoAdmin}
															buttonIcon={
																loadingMoreToDoAdmin ? (
																	<span className="btn-show-more-loading">
																		<LoadingSpinner show={true} isStatic width={18} height={18} />
																		Loading...
																	</span>
																) : (
																	<PlusIcon className="link-icon" />
																)
															}
														>
															{!loadingMoreToDoAdmin &&
																`Show ${Math.min(defaultMaxItems, adminToDoTotalCount - toDoAdminList.length)} more`}
														</Button>
													)}
												</>
											) : (
												<div className="empty-notification">
													<p className="sub-grey-text">No checklists to do.</p>
												</div>
											)}
										</section>

										<section className="info-section complaints-page">
											<div className="section-header bold">Overdue</div>
											{overdueAdminList.length > 0 ? (
												<>
													<div className="grid-list">
														{overdueAdminList.map((item) => (
															<div key={item.id} className="grid-list-row">
																<div className="grid-list-row__inner">
																	<div className="grid-cell-icon">
																		<StatusIcon statusType="danger" />
																	</div>

																	<div className="grid-cell-name">{item.purpose}</div>

																	<div className="grid-cell-created">
																		<span className="title body-medium">Created by</span>

																		<span className="name body-medium text-grey-4">{item.creator}</span>
																	</div>

																	<div className="grid-cell-date">
																		<span className="title body-medium">Due date</span>

																		<span className="status body-medium text-grey-4">
																			<DisplayDateTimeNoParse format="D/M/YYYY, h:mm a">{item.dueDate}</DisplayDateTimeNoParse>
																		</span>
																	</div>

																	<div className="grid-cell-action">
																		<Link className="btn-text pdl-0-mobile" to={`/safety-checklists/${item.id}`}>
																			View
																		</Link>
																	</div>
																</div>
															</div>
														))}
													</div>

													{overdueAdminList.length < adminOverdueTotalCount && (
														<Button
															className="btn-secondary btn-show-more"
															onClick={loadMoreAdminOverdue}
															disabled={loadingMoreOverdueAdmin}
															buttonIcon={
																loadingMoreOverdueAdmin ? (
																	<span className="btn-show-more-loading">
																		<LoadingSpinner show={true} isStatic width={18} height={18} />
																		Loading...
																	</span>
																) : (
																	<PlusIcon className="link-icon" />
																)
															}
														>
															{!loadingMoreOverdueAdmin &&
																`Show ${Math.min(defaultMaxItems, adminOverdueTotalCount - overdueAdminList.length)} more`}
														</Button>
													)}
												</>
											) : (
												<div className="empty-notification">
													<p className="sub-grey-text">No overdue checklists.</p>
												</div>
											)}
										</section>

										<section className="info-section complaints-page">
											<div className="section-header bold">Manage</div>

											{manageAdminList.length > 0 ? (
												<>
													<div className="grid-list">
														{manageAdminList.map((item) => (
															<div key={item.id} className="grid-list-row">
																<div className="grid-list-row__inner">
																	<div className="grid-cell-icon">
																		<StatusIcon statusType="manage" />
																	</div>

																	<div className="grid-cell-name">{item.title}</div>

																	<div className="grid-cell-created">
																		<span className="title body-medium">Created by</span>

																		<span className="name body-medium text-grey-4">{item.creator}</span>
																	</div>

																	<div className="grid-cell-date">
																		<span className="title body-medium">Created</span>

																		<span className="name body-medium text-grey-4">
																			<DisplayDateTime format="D/M/YYYY, h:mm a">{item.createdDate}</DisplayDateTime>
																		</span>
																	</div>

																	<div className="grid-cell-action">
																		<Link
																			className="btn-text pdl-0-mobile"
																			to={`/create-checklist/ConditionId=${item.id}`}
																		>
																			Edit
																		</Link>
																	</div>
																</div>
															</div>
														))}
													</div>

													{manageAdminList.length < adminManageTotalCount && (
														<Button
															className="btn-secondary btn-show-more"
															onClick={loadMoreAdminManage}
															disabled={loadingMoreManageAdmin}
															buttonIcon={
																loadingMoreManageAdmin ? (
																	<span className="btn-show-more-loading">
																		<LoadingSpinner show={true} isStatic width={18} height={18} />
																		Loading...
																	</span>
																) : (
																	<PlusIcon className="link-icon" />
																)
															}
														>
															{!loadingMoreManageAdmin &&
																`Show ${Math.min(defaultMaxItems, adminManageTotalCount - manageAdminList.length)} more`}
														</Button>
													)}
												</>
											) : (
												<div className="empty-notification">
													<p className="sub-grey-text">No checklist conditions to manage.</p>
												</div>
											)}
										</section>

										<section className="info-section complaints-page">
											<div className="section-header bold">Completed</div>

											{completedAdminList.length > 0 ? (
												<>
													<div className="grid-list">
														{completedAdminList.map((item) => (
															<div key={item.id} className="grid-list-row">
																<div className="grid-list-row__inner">
																	<div className="grid-cell-icon">
																		<StatusIcon
																			statusType={
																				item.status === DomainChecklistStatusType.Completed ? 'success' : 'warning'
																			}
																		/>
																	</div>

																	<div className="grid-cell-name">{item.purpose}</div>

																	<div className="grid-cell-created">
																		<span className="title body-medium">Completed by</span>

																		<span className="name body-medium text-grey-4">{item.completedBy}</span>
																	</div>

																	<div className="grid-cell-date">
																		<span className="title body-medium">Date</span>

																		<span className="status body-medium text-grey-4">
																			<DisplayDateTime format="D/M/YYYY, h:mm a">{item.updatedDate}</DisplayDateTime>
																		</span>
																	</div>

																	<div className="grid-cell-action">
																		<Link className="btn-text pdl-0-mobile" to={`/safety-checklists/${item.id}`}>
																			View
																		</Link>
																	</div>
																</div>
															</div>
														))}
													</div>

													{completedAdminList.length < adminCompletedTotalCount && (
														<Button
															className="btn-secondary btn-show-more"
															onClick={loadMoreAdminCompleted}
															disabled={loadingMoreCompletedAdmin}
															buttonIcon={
																loadingMoreCompletedAdmin ? (
																	<span className="btn-show-more-loading">
																		<LoadingSpinner show={true} isStatic width={18} height={18} />
																		Loading...
																	</span>
																) : (
																	<PlusIcon className="link-icon" />
																)
															}
														>
															{!loadingMoreCompletedAdmin &&
																`Show ${Math.min(defaultMaxItems, adminCompletedTotalCount - completedAdminList.length)} more`}
														</Button>
													)}
												</>
											) : (
												<div className="empty-notification">
													<p className="sub-grey-text">You have no completed checklists.</p>
												</div>
											)}
										</section>
									</>
								) : (
									<>
										<p className="sub-grey-text mbtom-30">
											Safety checklists are a critical risk management tool to manage safety and minimise the risk of
											harm to children in your organisation. You can create checklists or view your checklists.
										</p>

										{isOrganisationAdmin && (
											<Alert color="info" className="mb-5">
												<div className="alert-icon">
													<SwitchUserIcon />
												</div>

												<div className="info-text">
													<p className="mbtom-8">
														Below are safety checklists <strong>requiring action by you</strong>.
													</p>

													<p>
														As a <strong>Leader</strong>, you can also view all safety checklists created throughout
														your organisation. Click on <strong>'View all checklists'</strong> to switch roles.
													</p>
												</div>

												<Button className="btn-primary small ml-auto" onClick={() => switchRole()}>
													<SwitchIcon className="link-icon" />
													View all checklists
												</Button>
											</Alert>
										)}

										{isOrganisationActive && (
											<div
												className={
													toDoUserList.length ||
													overdueUserList.length ||
													manageUserList.length ||
													completedUserList.length
														? 'submit-btn-container'
														: 'submit-btn-container empty'
												}
											>
												<Link to="/new-checklist" className="btn-primary submit-btn">
													Create a checklist
												</Link>
											</div>
										)}

										<section className="info-section complaints-page">
											<div className="section-header bold">To-Do</div>

											{toDoUserList.length > 0 ? (
												<>
													<div className="grid-list">
														{toDoUserList.map((item) => (
															<div key={item.id} className="grid-list-row">
																<div className="grid-list-row__inner">
																	<div className="grid-cell-icon">
																		<StatusIcon
																			statusType={
																				item.status === DomainChecklistStatusType.Completed ? 'success' : 'warning'
																			}
																		/>
																	</div>

																	<div className="grid-cell-name">{item.purpose}</div>

																	<div className="grid-cell-created">
																		<span className="title body-medium">Created by</span>

																		<span className="name body-medium text-grey-4">{item.creator}</span>
																	</div>

																	<div className="grid-cell-date">
																		<span className="title body-medium">Created</span>

																		<span className="status body-medium text-grey-4">
																			<DisplayDateTime format="D/M/YYYY, h:mm a">{item.createdDate}</DisplayDateTime>
																		</span>
																	</div>

																	<div className="grid-cell-action">
																		<Link className="btn-text pdl-0-mobile" to={`/safety-checklists/${item.id}`}>
																			View
																		</Link>
																	</div>
																</div>
															</div>
														))}
													</div>

													{toDoUserList.length < userToDoTotalCount && (
														<Button
															className="btn-secondary btn-show-more"
															onClick={loadMoreUserToDo}
															disabled={loadingMoreToDoUser}
															buttonIcon={
																loadingMoreToDoUser ? (
																	<span className="btn-show-more-loading">
																		<LoadingSpinner show={true} isStatic width={18} height={18} />
																		Loading...
																	</span>
																) : (
																	<PlusIcon className="link-icon" />
																)
															}
														>
															{!loadingMoreToDoUser &&
																`Show ${Math.min(defaultMaxItems, userToDoTotalCount - toDoUserList.length)} more`}
														</Button>
													)}
												</>
											) : (
												<div className="empty-notification">
													<p className="sub-grey-text">No checklists to do.</p>
												</div>
											)}
										</section>

										<section className="info-section complaints-page">
											<div className="section-header bold">Overdue</div>
											{overdueUserList.length > 0 ? (
												<>
													<div className="grid-list">
														{overdueUserList.map((item) => (
															<div key={item.id} className="grid-list-row">
																<div className="grid-list-row__inner">
																	<div className="grid-cell-icon">
																		<StatusIcon statusType="danger" />
																	</div>

																	<div className="grid-cell-name">{item.purpose}</div>

																	<div className="grid-cell-created">
																		<span className="title body-medium">Created by</span>

																		<span className="name body-medium text-grey-4">{item.creator}</span>
																	</div>

																	<div className="grid-cell-date">
																		<span className="title body-medium">Due date</span>

																		<span className="status body-medium text-grey-4">
																			<DisplayDateTimeNoParse format="D/M/YYYY, h:mm a">{item.dueDate}</DisplayDateTimeNoParse>
																		</span>
																	</div>

																	<div className="grid-cell-action">
																		<Link className="btn-text pdl-0-mobile" to={`/safety-checklists/${item.id}`}>
																			View
																		</Link>
																	</div>
																</div>
															</div>
														))}
													</div>

													{overdueUserList.length < userOverdueTotalCount && (
														<Button
															className="btn-secondary btn-show-more"
															onClick={loadMoreUserOverdue}
															disabled={loadingMoreOverdueUser}
															buttonIcon={
																loadingMoreOverdueUser ? (
																	<span className="btn-show-more-loading">
																		<LoadingSpinner show={true} isStatic width={18} height={18} />
																		Loading...
																	</span>
																) : (
																	<PlusIcon className="link-icon" />
																)
															}
														>
															{!loadingMoreOverdueUser &&
																`Show ${Math.min(defaultMaxItems, userOverdueTotalCount - overdueUserList.length)} more`}
														</Button>
													)}
												</>
											) : (
												<div className="empty-notification">
													<p className="sub-grey-text">No overdue checklists.</p>
												</div>
											)}
										</section>

										<section className="info-section complaints-page">
											<div className="section-header bold">Manage</div>

											{manageUserList.length > 0 ? (
												<>
													<div className="grid-list">
														{manageUserList.map((item) => (
															<div key={item.id} className="grid-list-row">
																<div className="grid-list-row__inner">
																	<div className="grid-cell-icon">
																		<StatusIcon statusType="manage" />
																	</div>

																	<div className="grid-cell-name">{item.title}</div>

																	<div className="grid-cell-created">
																		<span className="title body-medium">Created by</span>

																		<span className="name body-medium text-grey-4">{item.creator}</span>
																	</div>

																	<div className="grid-cell-date">
																		<span className="title body-medium">Created</span>

																		<span className="name body-medium text-grey-4">
																			<DisplayDateTime format="D/M/YYYY, h:mm a">{item.createdDate}</DisplayDateTime>
																		</span>
																	</div>

																	<div className="grid-cell-action">
																		<Link
																			className="btn-text pdl-0-mobile"
																			to={`/create-checklist/ConditionId=${item.id}`}
																		>
																			Edit
																		</Link>
																	</div>
																</div>
															</div>
														))}
													</div>

													{manageUserList.length < userManageTotalCount && (
														<Button
															className="btn-secondary btn-show-more"
															onClick={loadMoreUserManage}
															disabled={loadingMoreManageUser}
															buttonIcon={
																loadingMoreManageUser ? (
																	<span className="btn-show-more-loading">
																		<LoadingSpinner show={true} isStatic width={18} height={18} />
																		Loading...
																	</span>
																) : (
																	<PlusIcon className="link-icon" />
																)
															}
														>
															{!loadingMoreManageUser &&
																`Show ${Math.min(defaultMaxItems, userManageTotalCount - manageUserList.length)} more`}
														</Button>
													)}
												</>
											) : (
												<div className="empty-notification">
													<p className="sub-grey-text">No checklist conditions to manage.</p>
												</div>
											)}
										</section>

										<section className="info-section complaints-page">
											<div className="section-header bold">Completed</div>

											{completedUserList.length > 0 ? (
												<>
													<div className="grid-list">
														{completedUserList.map((item) => (
															<div key={item.id} className="grid-list-row">
																<div className="grid-list-row__inner">
																	<div className="grid-cell-icon">
																		<StatusIcon
																			statusType={
																				item.status === DomainChecklistStatusType.Completed ? 'success' : 'warning'
																			}
																		/>
																	</div>

																	<div className="grid-cell-name">{item.purpose}</div>

																	<div className="grid-cell-created">
																		<span className="title body-medium">Completed by</span>

																		<span className="name body-medium text-grey-4">{item.completedBy}</span>
																	</div>

																	<div className="grid-cell-date">
																		<span className="title body-medium">Date</span>

																		<span className="status body-medium text-grey-4">
																			<DisplayDateTime format="D/M/YYYY, h:mm a">{item.updatedDate}</DisplayDateTime>
																		</span>
																	</div>

																	<div className="grid-cell-action">
																		<Link className="btn-text pdl-0-mobile" to={`/safety-checklists/${item.id}`}>
																			View
																		</Link>
																	</div>
																</div>
															</div>
														))}
													</div>

													{completedUserList.length < userCompletedTotalCount && (
														<Button
															className="btn-secondary btn-show-more"
															onClick={loadMoreUserCompleted}
															disabled={loadingMoreCompletedUser}
															buttonIcon={
																loadingMoreCompletedUser ? (
																	<span className="btn-show-more-loading">
																		<LoadingSpinner show={true} isStatic width={18} height={18} />
																		Loading...
																	</span>
																) : (
																	<PlusIcon className="link-icon" />
																)
															}
														>
															{!loadingMoreCompletedUser &&
																`Show ${Math.min(defaultMaxItems, userCompletedTotalCount - completedUserList.length)} more`}
														</Button>
													)}
												</>
											) : (
												<div className="empty-notification">
													<p className="sub-grey-text">You have no completed checklists.</p>
												</div>
											)}
										</section>
									</>
								)}

								<ScrollToTopButton offsetValue={100} />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default SafetyChecklists;
